import axios from '@fs/zion-axios'
import { showEx } from '@fs/zion-experiments'
import { baseUrl as initialBaseUrl, sgBaseUrl } from '@fs/zion-config'
import { getSessionId } from '@fs/zion-session'
import customError from '../serviceUtils'
import { headers, localhostUserAgent, SG_BASE_URL } from '../config'
import { errorCollector } from '../../utils/utils'

// must fall back while testing (localhost works fine, it's just Jest that falls back)
const baseUrl = initialBaseUrl || 'https://beta.familysearch.org'

const dzPath = '/dz/v1'

let deepZoomCloudBase = `${baseUrl}${dzPath}`
const onLocalhost = /localhost/.test(window.location.href)
if (onLocalhost) {
  deepZoomCloudBase = dzPath
}

/**
 * Get an image APID given an ARK
 * @param {String} ark ARK
 * @returns {Promise<any>}
 */
async function arkToApid(ark) {
  try {
    const callHeaders = onLocalhost
      ? {
          headers: { ...localhostUserAgent },
        }
      : null
    const response = await axios.get(`${baseUrl}/das/v2/${ark}/name?namespace=apid`, callHeaders)

    const { data: apid } = response

    return apid
  } catch (error) {
    errorCollector(error, ark, { codeId: 11, arkToApid: 'failed' })
    throw customError(error, 'Failed to get APID from ARK')
  }
}

async function getRmsId(partitionKey) {
  const apidEndpoint = `${SG_BASE_URL}/service/records/storage/dascloud/das/v2/${partitionKey}/name?namespace=apid`
  const apid = await axios.get(apidEndpoint, { headers: { ...headers, 'content-type': 'text/plain' } }).catch((err) => {
    errorCollector(err, partitionKey, {
      codeId: 50,
      status: err?.response?.status,
      responseBody: err?.response?.data,
    })
    throw new Error(err)
  })

  const rmsIdEndpoint = `${SG_BASE_URL}/service/records/rms/artifact/${apid.data}/parent/grouptype/NATURAL`
  const rmsResponse = await axios
    .get(rmsIdEndpoint, { headers: { ...headers, 'content-type': 'application/json' } })
    .catch((err) => {
      errorCollector(err, partitionKey, {
        codeId: 44,
        status: err?.response?.status,
        responseBody: err?.response?.data,
      })
      throw new Error(err)
    })
  return Array.isArray(rmsResponse.data) && rmsResponse.data.length > 0 ? rmsResponse.data[0] : ''
}

/**
 * Change the ark into an image url
 * @param {String} imageArk i.e. S7WF-SCS6-99
 * @returns {String} the image URL
 */
function getImageArkUrl(imageArk, dasProxy) {
  if (!imageArk) return ''
  const proxyStr = dasProxy ? '?proxy=true' : ''
  const indexingFileCacheEx = showEx('indexingFileCacheEx')
  if (indexingFileCacheEx && window.location.hostname === 'localhost' && imageArk === '3:1:3Q9M-CSMT-Y9P6') {
    return `http://${window.location.host}/TH-909-54807-1603-49/image.xml`
  }
  return `${deepZoomCloudBase}/${imageArk}/image.xml${proxyStr}`
}

/**
 * Change the ark into an image thumbnail url
 * @param {String} arkPath i.e. S7WF-SCS6-99
 * @returns {String} the image thumbnail URL
 */
function getImageThumbUrl(arkPath, dasProxy) {
  const proxyStr = dasProxy ? '?proxy=true' : ''

  return `${dzPath}/${arkPath}/thumb_p200.jpg${proxyStr}`
}

/**
 * Get the url for image apids
 * @param {string} apid         Image apid we are looking for
 * @param {string} type         Type of image we wan
 * @returns {string}            Url for the image
 */
function getApidImageUrl(apid, type) {
  // on localhost we need to pass in the sessionId param
  if (window.location.hostname === 'localhost') {
    const sessionId = getSessionId() ? `?sessionId=${getSessionId()}` : ''
    return `${sgBaseUrl}/service/records/storage/deepzoomcloud/dz/v1/apid:${apid}/${type}${sessionId}`
  }
  return `${sgBaseUrl}/service/records/storage/deepzoomcloud/dz/v1/apid:${apid}/${type}`
}

/**
 * Get the permissions for an image.
 * @param {String} partitionKey
 * @returns {Promise<any>} The permissions for the image e.g. ThemisPrmAnyone
 */
function getImagePermission(partitionKey) {
  return axios.get(`${sgBaseUrl}/service/records/storage/dascloud/das/v2/${partitionKey}/permission`)
}

/**
 * Get the permissions for a group.
 * @param {String} imageGroupNumber // This will not work for split groups
 * @returns {Promise<any>} The permissions for the group e.g. ThemisPrmAnyone
 */
function getGroupPermissions(imageGroupNumber) {
  return axios.get(`${sgBaseUrl}/service/records/storage/dascloud/das/v2/dgs:${imageGroupNumber}/permission`)
}

export {
  arkToApid,
  getImageArkUrl,
  getImageThumbUrl,
  getRmsId,
  getApidImageUrl,
  getImagePermission,
  getGroupPermissions,
}
